import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../../hooks/useCart';
import { reservationApi } from '../../lib/api';
import { useAuthStore } from '../../stores/auth';
import toast from 'react-hot-toast';

export default function CartBar() {
  const { cart, removeVehicle, clearCart } = useCart();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [submitting, setSubmitting] = useState(false);
  const count = cart.vehicles.length;

  if (count === 0) return null;

  const handleInquire = async () => {
    // Single vehicle: submit reservation directly if dates are set
    if (count === 1 && cart.pickupDate && cart.returnDate) {
      if (!user) { navigate('/signin'); return; }
      setSubmitting(true);
      try {
        const res = await reservationApi.create({
          vehicle_id: cart.vehicles[0].id,
          pickup_date: cart.pickupDate,
          return_date: cart.returnDate,
          pickup_location: cart.pickupLocation || undefined,
        });
        toast.success('예약이 요청되었습니다');
        clearCart();
        navigate(`/reservations/${res.id}`);
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : '예약 요청에 실패했습니다';
        toast.error(msg);
      } finally {
        setSubmitting(false);
      }
      return;
    }
    // Fallback: navigate to vehicle page
    if (count === 1) {
      navigate(`/vehicles/${cart.vehicles[0].id}`);
    } else {
      navigate(`/vehicles/${cart.vehicles[0].id}?cart=1`);
    }
  };

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 shadow-2xl">
      <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-3">
        {/* Vehicle badges */}
        <div className="flex-1 flex items-center gap-2 overflow-x-auto min-w-0">
          <span className="text-xs font-medium text-gray-400 shrink-0">
            장바구니 <strong className="text-slate-900">{count}</strong>
          </span>
          {cart.vehicles.map(v => (
            <span key={v.id}
              className="inline-flex items-center gap-1 px-2.5 py-1 bg-brand-50 border border-brand-200 rounded-full text-xs font-medium text-brand-700 shrink-0"
            >
              {v.make} {v.model}
              <button
                onClick={() => removeVehicle(v.id)}
                className="ml-0.5 w-4 h-4 rounded-full hover:bg-brand-200 flex items-center justify-center text-brand-400 hover:text-brand-700 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-brand-500"
              >
                ✕
              </button>
            </span>
          ))}
          {cart.pickupDate && (
            <span className="text-xs text-gray-400 shrink-0">
              {cart.pickupDate} ~ {cart.returnDate}
            </span>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={() => navigate('/')}
            className="px-3 py-1.5 text-sm text-gray-500 hover:text-gray-700 whitespace-nowrap transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-brand-500"
          >
            + 추가 차량
          </button>
          <button
            onClick={clearCart}
            className="px-3 py-1.5 text-sm text-gray-400 hover:text-red-500 whitespace-nowrap transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-brand-500"
          >
            비우기
          </button>
          <button
            onClick={handleInquire}
            disabled={submitting}
            className="px-5 py-2 bg-slate-900 hover:bg-slate-800 text-white font-semibold text-sm rounded-xl transition-colors duration-150 whitespace-nowrap focus:outline-none focus:ring-2 focus:ring-brand-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? '처리 중...' : `견적 문의하기 (${count})`}
          </button>
        </div>
      </div>
    </div>
  );
}
